/**
 * RelatedItems — the reusable "Related across the platform" panel.
 *
 * Given any key entity it fetches the cross-feature linkage resolver
 * (/api/cyber/links/{type}/{key}) and renders the related items grouped by
 * relation. Each row is clickable and navigates to the link's route, so a click
 * on a CVE / race / NBA action / finding / scenario / advisory / eval / gap
 * surfaces its provenance chain:
 *
 *   "this losing race → feeds this Next Best Action → was attacked in this
 *    purple-range scenario → the CVE is behind target in Distance-to-N →
 *    cited in this NCSC report."
 *
 * Self-contained: it owns its fetch + loading skeleton + empty state, so a page
 * embeds it with two props and nothing else. Fail-soft — a fetch error renders
 * the same calm empty state, never an error wall.
 */
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { clsx } from 'clsx'
import {
  Bug, Flag, Sparkles, Swords, Crosshair, ShieldAlert,
  Target, ArrowRight, Link2, ExternalLink,
} from 'lucide-react'
import { api } from '../lib/api'

export type EntityType =
  | 'cve' | 'race' | 'nba_action' | 'reasoned_finding'
  | 'purple_scenario' | 'ncsc_advisory' | 'aisi_eval' | 'target_gap'

interface CrossLink {
  type: string
  key: string
  title: string
  route: string
  relation: string
  why: string
}

interface RelatedItemsProps {
  entityType: EntityType
  entityKey: string
  className?: string
}

// Icon per link type.
const TYPE_ICON: Record<string, typeof Bug> = {
  cve: Bug,
  race: Swords,
  nba_action: Flag,
  reasoned_finding: Sparkles,
  purple_scenario: Crosshair,
  ncsc_advisory: ShieldAlert,
  aisi_eval: Sparkles,
  target_gap: Target,
}

// Tone per relation (chip colour).
const RELATION_TONE: Record<string, string> = {
  feeds: 'bg-blue/15 text-blue-light border-blue/30',
  'attacked-in': 'bg-rose/15 text-rose border-rose/30',
  'behind-target': 'bg-amber/15 text-amber-light border-amber/30',
  'cited-in': 'bg-violet/15 text-violet-light border-violet/30',
  'exploited-by': 'bg-rose/15 text-rose border-rose/30',
  'covered-by': 'bg-emerald/15 text-emerald border-emerald/30',
}

const RELATION_LABEL: Record<string, string> = {
  feeds: 'feeds',
  'attacked-in': 'attacked in',
  'behind-target': 'behind target',
  'cited-in': 'cited in',
  'exploited-by': 'exploited by',
  'covered-by': 'covered by',
}

// Stable order so chains read top-to-bottom in a sensible provenance order.
const RELATION_ORDER = [
  'exploited-by', 'feeds', 'attacked-in', 'behind-target', 'cited-in', 'covered-by',
]

function isExternal(route: string): boolean {
  return /^https?:\/\//i.test(route)
}

export default function RelatedItems({
  entityType, entityKey, className,
}: RelatedItemsProps) {
  const navigate = useNavigate()
  const [links, setLinks] = useState<CrossLink[] | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let alive = true
    if (!entityKey) { setLinks([]); setLoading(false); return }
    setLoading(true)
    api
      .get(`/api/cyber/links/${entityType}/${encodeURIComponent(entityKey)}`)
      .then((r) => { if (alive) setLinks(r.data?.links || []) })
      .catch(() => { if (alive) setLinks([]) }) // fail-soft → calm empty state
      .finally(() => { if (alive) setLoading(false) })
    return () => { alive = false }
  }, [entityType, entityKey])

  const go = (route: string) => {
    if (isExternal(route)) window.open(route, '_blank', 'noopener,noreferrer')
    else navigate(route)
  }

  // Group by relation, in RELATION_ORDER.
  const grouped: Record<string, CrossLink[]> = {}
  for (const l of links || []) (grouped[l.relation] ||= []).push(l)
  const relations = Object.keys(grouped).sort(
    (a, b) => {
      const ia = RELATION_ORDER.indexOf(a)
      const ib = RELATION_ORDER.indexOf(b)
      return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib)
    })

  return (
    <div className={clsx(
      'rounded-lg border border-border bg-surface-raised/40 p-4', className)}>
      <div className="mb-3 flex items-center gap-2">
        <Link2 className="h-4 w-4 text-text-muted" />
        <h3 className="text-sm font-semibold text-text">Related across the platform</h3>
        {links && links.length > 0 && (
          <span className="ml-auto rounded-full bg-surface-raised px-2 py-0.5 text-[11px] text-text-muted">
            {links.length}
          </span>
        )}
      </div>

      {loading ? (
        <div className="space-y-2" aria-busy="true">
          {[0, 1, 2].map((i) => (
            <div key={i} className="flex items-center gap-2">
              <div className="h-4 w-4 animate-pulse rounded bg-surface-raised" />
              <div className="h-4 flex-1 animate-pulse rounded bg-surface-raised" />
            </div>
          ))}
        </div>
      ) : !links || links.length === 0 ? (
        <p className="py-2 text-sm text-text-muted">No cross-links found yet.</p>
      ) : (
        <div className="space-y-3">
          {relations.map((rel) => (
            <div key={rel}>
              <div className="mb-1.5 text-[11px] font-medium uppercase tracking-wide text-text-muted">
                {RELATION_LABEL[rel] || rel.replace(/-/g, ' ')}
              </div>
              <ul className="space-y-1">
                {grouped[rel].map((l, idx) => {
                  const Icon = TYPE_ICON[l.type] || Bug
                  return (
                    <li key={`${l.type}:${l.key}:${idx}`}>
                      <button
                        type="button"
                        onClick={() => go(l.route)}
                        className="group flex w-full items-start gap-2 rounded-md border border-transparent px-2 py-1.5 text-left transition hover:border-border hover:bg-surface-raised"
                      >
                        <Icon className="mt-0.5 h-4 w-4 shrink-0 text-text-muted group-hover:text-text" />
                        <span className="min-w-0 flex-1">
                          <span className="flex items-center gap-2">
                            <span className="truncate text-sm text-text">{l.title}</span>
                            <span className={clsx(
                              'shrink-0 rounded border px-1.5 py-0.5 text-[10px] font-medium',
                              RELATION_TONE[l.relation] || 'bg-surface-raised text-text-muted border-border')}>
                              {RELATION_LABEL[l.relation] || l.relation}
                            </span>
                          </span>
                          {l.why && (
                            <span className="mt-0.5 block truncate text-xs text-text-muted">
                              {l.why}
                            </span>
                          )}
                        </span>
                        {isExternal(l.route)
                          ? <ExternalLink className="mt-0.5 h-3.5 w-3.5 shrink-0 text-text-muted opacity-0 transition group-hover:opacity-100" />
                          : <ArrowRight className="mt-0.5 h-3.5 w-3.5 shrink-0 text-text-muted opacity-0 transition group-hover:opacity-100" />}
                      </button>
                    </li>
                  )
                })}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
